import { useFormikContext } from 'formik';
import {
  Button,
  FilterDropdownItem,
  Icons,
  InputField,
  MultiSelectFilterDropdown,
  Tooltip,
} from 'plume-ui';
import { FunctionComponent, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';

import { useFeaturePermission } from '../../../../../../../hooks/useFeaturePermission';

import { outreachTestMessageModalAtom } from '../../../../../../../state';
import { Maybe } from '../../../../../../../types';
import { OutreachTypes } from '../../../../../../../urls';
import { Permission } from '../../../../../../../userPermissionConfig';
import FormattedMessage from '../../../../../../../utils/components/FormattedMessage';
import OutreachConfigFieldMessages from '../../../../../../Outreach/components/OutreachConfigFieldMessages';
import {
  FormikMessageField,
  OutreachConfigGenericFormField,
  OutreachConfigSections,
  SupportOutreachContentFormFields,
} from '../../../../../types';
import OutreachConfigSection from '../../../OutreachConfigSection';
import useFormControls from '../../../hooks/useFormControls';
import useValidation from '../../../hooks/useValidation';
import { parseEmailsToDropdownOptions } from './helpers';
import useEmails from '../../../../../../Configuration/hooks/useEmails';

export type FormikSupportOutreachContentValue = {
  outreachRuleName: string;
  outreachMethod: boolean;
  supportOutreachTime?: {
    from: Date;
    to: Date;
  };
} & FormikMessageField;

const FormikSupportOutreachContentField: FunctionComponent<OutreachConfigGenericFormField<
  FormikSupportOutreachContentValue
>> = ({ field, form }) => {
  const { t } = useTranslation();
  const { setPropertyValue } = useFormControls<
    FormikSupportOutreachContentValue
  >(field, form);
  const { setFieldValue, getFieldProps } = useFormikContext();
  const { hasPermission } = useFeaturePermission();
  const titleCopyId = 'outreach.form.supportOutreachContentSection.title';
  const subtitleCopyId = 'outreach.form.supportOutreachContentSection.subtitle';
  const { isSectionComplete } = useValidation();

  const setOutreachTestMessageModal = useSetRecoilState(
    outreachTestMessageModalAtom,
  );
  const canEditSupportEmail = hasPermission(
    Permission.EditSupportEmailOutreach,
  );
  const [recipientsList, setRecipientsList] = useState<
    Maybe<FilterDropdownItem[]>
  >(undefined);
  const [selectedEmails, setSelectedEmails] = useState<
    Maybe<FilterDropdownItem[]>
  >(undefined);
  const { isLoading: loadingEmails, data: emailList } = useEmails();

  useEffect(() => {
    if (loadingEmails || !emailList) return;
    setRecipientsList(parseEmailsToDropdownOptions(emailList));
  }, [emailList, loadingEmails]);

  useEffect(() => {
    if (
      emailList === undefined ||
      !field.value[SupportOutreachContentFormFields.Recipients].length
    )
      return;
    setSelectedEmails(
      parseEmailsToDropdownOptions(
        emailList?.filter(
          (email) =>
            field.value[SupportOutreachContentFormFields.Recipients].includes(
              email.name,
            ) ||
            field.value[SupportOutreachContentFormFields.Recipients].includes(
              email.email,
            ) ||
            field.value[SupportOutreachContentFormFields.Recipients].includes(
              email.id,
            ),
        ),
      ),
    );
  }, [emailList]);

  const renderMessageTemplate = (): string[] => {
    const keys: {
      [key: string]: string;
    } = t('outreach.form.supportOutreachContentSection.templateLines', {
      returnObjects: true,
    });
    return Object.keys(keys).map((key) => keys[key as keyof typeof keys]);
  };

  const isComplete = isSectionComplete(
    OutreachConfigSections.SupportOutreachContent,
  );

  const onClickTestNotification = () => {
    setOutreachTestMessageModal({
      isOpen: true,
      messageType: 'issue',
      outreachType: OutreachTypes.Email,
      ctaUrl: undefined,
    });
  };

  useEffect(() => {
    if (
      recipientsList === undefined ||
      !recipientsList.length ||
      selectedEmails === undefined
    )
      return;
    setPropertyValue(
      SupportOutreachContentFormFields.Recipients,
      selectedEmails.map((item) => item.value),
    );
    setFieldValue(OutreachConfigSections.SupportOutreachClearMessage, {
      ...getFieldProps(OutreachConfigSections.SupportOutreachClearMessage)
        .value,
      recipients: selectedEmails.map((item) => item.value),
    });
  }, [selectedEmails, recipientsList]);

  return (
    <OutreachConfigSection
      title={t(titleCopyId)}
      subtitle={t(subtitleCopyId)}
      isRequired
      isComplete={isComplete}
      isOpen={canEditSupportEmail}
    >
      <div className="OutreachContainer__section-content-wrapper">
        <div className="OutreachContainer__outreach-content-row OutreachContainer__outreach-content-row--horizontal-form">
          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <span className="color-required">* </span>
              <FormattedMessage id="outreach.form.supportOutreachContentSection.outreachRuleName" />
            </div>
            <div className="OutreachContainer__outreach-content-field-input">
              <InputField
                name={SupportOutreachContentFormFields.OutreachRuleName}
                type="text"
                value={
                  field.value[SupportOutreachContentFormFields.OutreachRuleName]
                }
                onInput={(e) =>
                  setPropertyValue(
                    SupportOutreachContentFormFields.OutreachRuleName,
                    e.currentTarget.value,
                  )
                }
              />
              <OutreachConfigFieldMessages
                propertyName={SupportOutreachContentFormFields.OutreachRuleName}
                field={field}
                form={form}
              />
            </div>
          </div>

          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <span className="color-required">* </span>
              <FormattedMessage id="outreach.form.supportOutreachContentSection.recipient" />
            </div>
            <div className="OutreachContainer__outreach-content-field-list">
              {recipientsList !== undefined && (
                <div>
                  <MultiSelectFilterDropdown
                    defaultLabel={t(
                      'outreach.form.supportOutreachContentSection.recipientDropdownLabel',
                    )}
                    selectedItems={selectedEmails || []}
                    onSelect={(emails) => {
                      setSelectedEmails([...emails]);
                    }}
                    searchBar={recipientsList.length > 10}
                    openInPortal
                    expandDirection="auto"
                    containerLabel=""
                    multiItemsSelectedLabel=""
                    items={recipientsList}
                    closeOnItemClick={false}
                    noItemsWarning={t(
                      'outreach.form.supportOutreachContentSection.noEmailsAvailable',
                    )}
                  />
                  <OutreachConfigFieldMessages
                    propertyName={SupportOutreachContentFormFields.Recipients}
                    field={field}
                    form={form}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <span className="color-required">* </span>
              <FormattedMessage id="outreach.form.supportOutreachContentSection.subject" />
            </div>
            <div className="OutreachContainer__outreach-content-field-input">
              <InputField
                name={SupportOutreachContentFormFields.Subject}
                type="text"
                value={field.value[SupportOutreachContentFormFields.Subject]}
                onInput={(e) =>
                  setPropertyValue(
                    SupportOutreachContentFormFields.Subject,
                    e.currentTarget.value,
                  )
                }
              />
              <OutreachConfigFieldMessages
                propertyName={SupportOutreachContentFormFields.Subject}
                field={field}
                form={form}
              />
            </div>
          </div>

          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <span className="color-required">* </span>
              <FormattedMessage id="outreach.form.supportOutreachContentSection.introduction" />
            </div>
            <div className="OutreachContainer__outreach-content-field-input">
              <InputField
                onInput={(e) =>
                  setPropertyValue(
                    SupportOutreachContentFormFields.Introduction,
                    e.currentTarget.value,
                  )
                }
                value={
                  field.value[SupportOutreachContentFormFields.Introduction]
                }
              />
              <OutreachConfigFieldMessages
                propertyName={SupportOutreachContentFormFields.Introduction}
                field={field}
                form={form}
              />
            </div>
          </div>
          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <FormattedMessage id="outreach.form.supportOutreachContentSection.messageTemplate" />
            </div>
            <div className="OutreachContainer__outreach-content-field-content">
              {renderMessageTemplate().map((line: string) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </div>
          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <FormattedMessage id="outreach.form.supportOutreachContentSection.test" />
            </div>
            <div className="OutreachContainer__outreach-content-field-content">
              <div className="OutreachContainer__outreach-content-testNotification">
                <Button
                  disabled={!form.values.supportOutreachTime?.from}
                  styleVariant="quaternary"
                  onClick={() => onClickTestNotification()}
                >
                  <FormattedMessage id="outreach.form.supportOutreachContentSection.testNotification" />
                </Button>

                {!form.values.supportOutreachTime?.from && (
                  <Tooltip
                    openInPortal
                    label={t(
                      'outreach.form.supportOutreachContentSection.testDisabledTooltip',
                    )}
                  >
                    <Icons.InfoIcon color="white" width={14} height={14} />
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </OutreachConfigSection>
  );
};

export default memo(FormikSupportOutreachContentField);
